import { createContext, FunctionComponent, ReactNode, useContext, useMemo } from 'react'

/**
 * Report content overrides.
 *
 * Every editable text block in a report (headings, narrative paragraphs and the
 * auto-generated data sentences) has a stable key. The backend stores only the
 * blocks a user has actually overridden (`sectionContent`), so a blank/absent
 * value means "use the built-in default" and the report renders exactly as it
 * did before anyone edited it.
 *
 * Overrides are plain text and may contain `{{token}}` placeholders (e.g.
 * `{{company}}`, `{{year}}`) that are substituted at render time so overridden
 * copy stays correct year-to-year. Unknown tokens are left verbatim.
 */

export type ReportContentTokens = Record<string, string | number | null | undefined>

/** Substitute `{{token}}` placeholders. Unknown tokens are left untouched. */
export const applyContentTokens = (text: string, tokens: ReportContentTokens): string =>
  text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, name: string) => {
    const value = tokens[name]
    return value === undefined || value === null ? match : String(value)
  })

interface ReportContentContextValue {
  overrides: Record<string, string>
  tokens: ReportContentTokens
}

const ReportContentContext = createContext<ReportContentContextValue>({ overrides: {}, tokens: {} })

export const ReportContentProvider: FunctionComponent<{
  overrides?: Record<string, string> | null
  tokens?: ReportContentTokens
  children: ReactNode
}> = ({ overrides, tokens, children }) => {
  const value = useMemo<ReportContentContextValue>(
    () => ({ overrides: overrides ?? {}, tokens: tokens ?? {} }),
    [overrides, tokens]
  )
  return <ReportContentContext.Provider value={value}>{children}</ReportContentContext.Provider>
}

/**
 * Resolve a content override for `key`. Returns the token-substituted override
 * string, or `undefined` when the block has not been overridden (so callers can
 * fall back to their built-in default with `?? <default>`).
 *
 * `localTokens` are merged over the report-wide tokens, letting data sentences
 * expose block-specific values (e.g. `{{topLabel}}`, `{{topValue}}`).
 */
export const useReportContentOverride = (
  key: string,
  localTokens?: ReportContentTokens
): string | undefined => {
  const { overrides, tokens } = useContext(ReportContentContext)
  const raw = overrides?.[key]
  if (raw == null || String(raw).trim() === '') return undefined
  const merged = localTokens ? { ...tokens, ...localTokens } : tokens
  return applyContentTokens(String(raw), merged)
}
